import { useEffect, useState } from 'react';
import { Link as ReactDomLink, useNavigate } from 'react-router-dom';
import {
  getMessageFromError,
  isAxiosErrorMatchedStatus,
} from '../../../api/axios';
import { doCreateUser } from '../../../api/usersService';
import AppLoading from '../../../components/App/AppLoading';
import {
  MBox,
  MButton,
  MHeading,
  MLink,
  MPageContainer,
  MText,
} from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import { useAuth } from '../../../services/auth0';
import Sentry from '../../../services/sentry';

const CreateUserConflictError = ({ message }: { message: string }) => {
  return (
    <MPageContainer>
      <MBox
        display="flex"
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
        flex={1}
      >
        <MHeading fontSize="3xl" mb="4" color="tPurple.base">
          Account Conflict
        </MHeading>
        <MText textAlign="center" mb="4">
          {message}
        </MText>
        <MButton variant="secondary" my="4" asChild>
          <ReactDomLink to={ROUTES.AUTH_LOGOUT}>Back to Login</ReactDomLink>
        </MButton>
      </MBox>
    </MPageContainer>
  );
};

const CreateUser = () => {
  const { getDecodedToken, getAndHandleAccessTokenSilently, userId } =
    useAuth();

  const navigate = useNavigate();
  const [conflictError, setConflictError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (userId) {
        // If userId is set, don't have to create again.
        Sentry.logger.info('CreateUser - user already exists, redirecting');
        navigate(ROUTES.ROOT);
        return;
      }

      const { updatedAccessToken, updatedUserId } =
        await getAndHandleAccessTokenSilently({ forceRefresh: true });

      if (updatedUserId) {
        // if user id is there from new token, do not create a user
        navigate(ROUTES.ROOT);
        return;
      }

      const tokenDecoded = getDecodedToken(updatedAccessToken);

      if (tokenDecoded) {
        try {
          Sentry.logger.info('CreateUser - creating new user');
          await doCreateUser(tokenDecoded.sub);
        } catch (err) {
          if (isAxiosErrorMatchedStatus(err, 409)) {
            const message = getMessageFromError(
              err,
              'An account conflict occurred. Please sign in with your original login method or contact your administrator.',
            );
            setConflictError(message);
            return;
          }
          throw err;
        }
        const { updatedUserId: updatedUserId1 } =
          await getAndHandleAccessTokenSilently({ forceRefresh: true });
        if (updatedUserId1) {
          Sentry.logger.info('CreateUser - user created successfully', {
            userId: updatedUserId1,
          });
          navigate(ROUTES.ROOT);
        }
      }
    };

    run();
  }, []);

  if (conflictError) {
    return <CreateUserConflictError message={conflictError} />;
  }

  return (
    <AppLoading loadingText="Creating a new user...">
      <MText my="4" fontSize="sm">
        You can also{' '}
        <MLink fontSize="sm" asChild>
          <ReactDomLink to={ROUTES.AUTH_LOGOUT}>log out</ReactDomLink>
        </MLink>
        .
      </MText>
    </AppLoading>
  );
};

export default CreateUser;
